// Photo caching and outbox storage operations

import { 
    STORES, 
    getAllFromStore, 
    getFromStore, 
    putToStore, 
    deleteFromStore,
    getByIndex 
} from './db-core';

interface CachedPhotoMetadata {
    blobName: string;
    surveyId: string;
    fieldId: string;
    contentType: string;
    fileSizeBytes: number;
    capturedAt: string;
    isUploaded: boolean;
    uploadedAt?: string;
}

interface CachedPhotoRecord {
    blobName: string;
    surveyId: string;
    fieldId: string;
    contentType: string;
    imageData: Blob;
    thumbnailData: Blob;
    fileSizeBytes: number;
    capturedAt: string;
    isUploaded: boolean;
    uploadedAt?: string;
}

interface PhotoOutboxItem {
    blobName: string;
    surveyId: string;
    reportId: string;
    fieldId: string;
    status: number;
    addedAt: string;
    attemptCount: number;
    lastError?: string;
    fileSizeBytes: number;
}

export const photoStorage = {
    async cachePhoto(
        blobName: string,
        surveyId: string,
        fieldId: string,
        contentType: string,
        imageBlob: Blob,
        thumbnailBlob: Blob
    ): Promise<boolean> {
        try {
            const record: CachedPhotoRecord = {
                blobName,
                surveyId,
                fieldId,
                contentType,
                imageData: imageBlob,
                thumbnailData: thumbnailBlob,
                fileSizeBytes: imageBlob.size,
                capturedAt: new Date().toISOString(),
                isUploaded: false
            };

            await putToStore(STORES.PHOTO_CACHE, record);
            return true;
        } catch (error) {
            console.error('[PhotoStorage] cachePhoto failed:', error);
            return false;
        }
    },

    async getImageBlob(blobName: string): Promise<Blob | null> {
        try {
            const record = await getFromStore<CachedPhotoRecord>(STORES.PHOTO_CACHE, blobName);
            return record?.imageData || null;
        } catch (error) {
            console.error('[PhotoStorage] getImageBlob failed:', error);
            return null;
        }
    },

    async getThumbnailBlob(blobName: string): Promise<Blob | null> {
        try {
            const record = await getFromStore<CachedPhotoRecord>(STORES.PHOTO_CACHE, blobName);
            return record?.thumbnailData || null;
        } catch (error) {
            console.error('[PhotoStorage] getThumbnailBlob failed:', error);
            return null;
        }
    },

    async getImageAsBytes(blobName: string): Promise<Uint8Array | null> {
        try {
            const record = await getFromStore<CachedPhotoRecord>(STORES.PHOTO_CACHE, blobName);
            if (!record) {
                return null;
            }
            const arrayBuffer = await record.imageData.arrayBuffer();
            return new Uint8Array(arrayBuffer);
        } catch (error) {
            console.error('[PhotoStorage] getImageAsBytes failed:', error);
            return null;
        }
    },

    async getPhotoMetadata(blobName: string): Promise<CachedPhotoMetadata | null> {
        try {
            const record = await getFromStore<CachedPhotoRecord>(STORES.PHOTO_CACHE, blobName);
            if (!record) {
                return null;
            }
            return {
                blobName: record.blobName,
                surveyId: record.surveyId,
                fieldId: record.fieldId,
                contentType: record.contentType,
                fileSizeBytes: record.fileSizeBytes,
                capturedAt: record.capturedAt,
                isUploaded: record.isUploaded,
                uploadedAt: record.uploadedAt
            };
        } catch (error) {
            console.error('[PhotoStorage] getPhotoMetadata failed:', error);
            return null;
        }
    },

    async deleteCachedPhoto(blobName: string): Promise<boolean> {
        try {
            await deleteFromStore(STORES.PHOTO_CACHE, blobName);
            return true;
        } catch (error) {
            console.error('[PhotoStorage] deleteCachedPhoto failed:', error);
            return false;
        }
    },

    async getPhotosForSurvey(surveyId: string): Promise<CachedPhotoMetadata[]> {
        try {
            const records = await getByIndex<CachedPhotoRecord>(STORES.PHOTO_CACHE, 'surveyId', surveyId);
            return records.map(r => ({
                blobName: r.blobName,
                surveyId: r.surveyId,
                fieldId: r.fieldId,
                contentType: r.contentType,
                fileSizeBytes: r.fileSizeBytes,
                capturedAt: r.capturedAt,
                isUploaded: r.isUploaded,
                uploadedAt: r.uploadedAt
            }));
        } catch (error) {
            console.error('[PhotoStorage] getPhotosForSurvey failed:', error);
            return [];
        }
    },

    async addToOutbox(itemJson: string): Promise<boolean> {
        try {
            const item = JSON.parse(itemJson) as PhotoOutboxItem;
            await putToStore(STORES.PHOTO_OUTBOX, item);
            return true;
        } catch (error) {
            console.error('[PhotoStorage] addToOutbox failed:', error);
            return false;
        }
    },

    async getOutboxItems(): Promise<string> {
        try {
            const items = await getAllFromStore<PhotoOutboxItem>(STORES.PHOTO_OUTBOX);
            return JSON.stringify(items);
        } catch (error) {
            console.error('[PhotoStorage] getOutboxItems failed:', error);
            return '[]';
        }
    },

    async getOutboxCount(): Promise<number> {
        try {
            const items = await getAllFromStore<PhotoOutboxItem>(STORES.PHOTO_OUTBOX);
            return items.filter(i => i.status === 1 || i.status === 4).length;
        } catch (error) {
            console.error('[PhotoStorage] getOutboxCount failed:', error);
            return 0;
        }
    },

    async getOutboxTotalBytes(): Promise<number> {
        try {
            const items = await getAllFromStore<PhotoOutboxItem>(STORES.PHOTO_OUTBOX);
            return items
                .filter(i => i.status === 1 || i.status === 4)
                .reduce((sum, i) => sum + i.fileSizeBytes, 0);
        } catch (error) {
            console.error('[PhotoStorage] getOutboxTotalBytes failed:', error);
            return 0;
        }
    },

    async updateOutboxStatus(blobName: string, status: number, errorMessage?: string): Promise<boolean> {
        try {
            const item = await getFromStore<PhotoOutboxItem>(STORES.PHOTO_OUTBOX, blobName);
            if (item) {
                item.status = status;
                if (status === 4) {
                    item.attemptCount++;
                    item.lastError = errorMessage || undefined;
                }
                await putToStore(STORES.PHOTO_OUTBOX, item);
            }
            return true;
        } catch (error) {
            console.error('[PhotoStorage] updateOutboxStatus failed:', error);
            return false;
        }
    },

    async removeFromOutbox(blobName: string): Promise<boolean> {
        try {
            await deleteFromStore(STORES.PHOTO_OUTBOX, blobName);
            return true;
        } catch (error) {
            console.error('[PhotoStorage] removeFromOutbox failed:', error);
            return false;
        }
    },

    async markAsUploaded(blobName: string): Promise<boolean> {
        try {
            const record = await getFromStore<CachedPhotoRecord>(STORES.PHOTO_CACHE, blobName);
            if (record) {
                record.isUploaded = true;
                record.uploadedAt = new Date().toISOString();
                await putToStore(STORES.PHOTO_CACHE, record);
            }
            await deleteFromStore(STORES.PHOTO_OUTBOX, blobName);
            return true;
        } catch (error) {
            console.error('[PhotoStorage] markAsUploaded failed:', error);
            return false;
        }
    }
};
